import { removeTimeZone, swaDate } from "@swa-ui/date";

const PICKUP_OFFSET_MINUTES = 45;
const RETURN_OFFSET_HOURS = 1;
const DEFAULT_RENTAL_DAYS = 3;

/**
 * Rounds a dayjs instance up to the next whole hour when any sub-hour component is set,
 * otherwise returns the start of the current hour.
 *
 * @param {import("dayjs").Dayjs} dateTime
 * @returns {import("dayjs").Dayjs}
 */
const roundUpToHour = (dateTime) =>
  dateTime.minute() || dateTime.second() || dateTime.millisecond()
    ? dateTime.add(1, "hour").startOf("hour")
    : dateTime.startOf("hour");

/**
 * Flattens an array of segment objects into a single merged stopsDetails array.
 *
 * @param {Object[]} segments Array of segment objects each with a `stopsDetails` array.
 * @returns {Object[]} Merged array of stop detail objects.
 */
const flattenStops = (segments = []) => segments.flatMap((segment) => segment.stopsDetails ?? []);

/**
 * Derives the car pickup location and date/time from the outbound flight segments.
 * The pickup time is the last outbound stop's arrival time plus 45 minutes, rounded up to
 * the nearest hour.
 *
 * @param {Object[]} outboundSegments Array of outbound segment objects.
 * @returns {{ iataCode: string, dateTime: Date }|null} Pickup location params, or null when data is missing.
 */
const getPickupLocation = (outboundSegments) => {
  const lastStop = flattenStops(outboundSegments).at(-1);
  const arrival =
    lastStop?.arrivalDateTime &&
    swaDate(removeTimeZone(lastStop.arrivalDateTime)).add(PICKUP_OFFSET_MINUTES, "minute");
  const pickupDateTime = arrival && roundUpToHour(arrival);
  const pickupLocation = pickupDateTime && {
    dateTime: pickupDateTime.toDate(),
    iataCode: lastStop.destinationAirportCode,
  };

  return pickupLocation || null;
};

/**
 * Derives the car return location and date/time from the inbound flight segments and pickup time.
 * For round trips, the return time is the first inbound stop's departure time minus 1 hour,
 * rounded down to the nearest hour. When that would precede the pickup time, or when no inbound
 * segments exist, the return defaults to pickup plus 3 days.
 *
 * @param {Object[]} inboundSegments Array of inbound segment objects.
 * @param {{ iataCode: string, dateTime: Date }} pickupLocation The resolved pickup location.
 * @returns {{ iataCode: string, dateTime: Date }} Return deeplink params.
 */
const getReturnDeeplinkParams = (inboundSegments, pickupLocation) => {
  const firstStop = flattenStops(inboundSegments)[0];
  const pickupDateTime = swaDate(pickupLocation.dateTime);
  const returnCandidate =
    firstStop?.departureDateTime &&
    swaDate(removeTimeZone(firstStop.departureDateTime))
      .subtract(RETURN_OFFSET_HOURS, "hour")
      .startOf("hour");
  const returnDateTime =
    returnCandidate && !returnCandidate.isBefore(pickupDateTime)
      ? returnCandidate
      : pickupDateTime.add(DEFAULT_RENTAL_DAYS, "day");

  return {
    dateTime: returnDateTime.toDate(),
    iataCode: firstStop?.originationAirportCode ?? pickupLocation.iataCode,
  };
};

/**
 * Derives CarTrawler deeplink location params from NextGen flight segments.
 *
 * Accepts the same two-element array of bound segment arrays used by
 * {@link getFlightInformationForCarSection}. Returns a `pickupLocation` and `returnLocation`
 * object ready to spread into `generateDeeplink`, or `null` when the outbound segments do not
 * contain enough data to determine a pickup time and airport.
 *
 * @param {Array[]} flightSegments Array of bounds, each bound being an array of segment objects.
 *   `flightSegments[0]` is the outbound bound; `flightSegments[1]` is the optional return bound.
 * @returns {{ pickupLocation: { iataCode: string, dateTime: Date }, returnLocation: { iataCode: string, dateTime: Date } }|null}
 */
export const getCarTrawlerDeeplinkParamsFromSegments = (flightSegments = []) => {
  const [outboundSegments = [], inboundSegments = []] = flightSegments;
  const pickupLocation = getPickupLocation(outboundSegments);
  const params = pickupLocation && {
    pickupLocation,
    returnLocation: getReturnDeeplinkParams(inboundSegments, pickupLocation),
  };

  return params || null;
};
